import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '~env';
import { readStoredToken } from '~shared/services/auth/auth-storage.util';
import { readCache, writeCache } from '~shared/i18n/i18n-cache';

/** Arbre de catalogue i18n imbriqué (feuilles = chaînes). */
export interface TranslationCatalog {
  [key: string]: string | TranslationCatalog;
}

/** Un catalogue imbriqué par code langue (`fr`, `en`, …). */
type LocaleCatalogs = Record<string, TranslationCatalog>;

/**
 * Réponse des endpoints de catalogue d'api-data (`/translation`, `/me/translation`).
 *
 * Contrat multi-locale : `locales` indexe chaque catalogue par code langue.
 * Sans `?locale=`, TOUTES les langues sont renvoyées d'un coup — c'est ce qu'on
 * exploite pour ne faire qu'un seul appel réseau au chargement.
 */
interface TranslationCatalogResponse {
  locales: LocaleCatalogs;
  generated_at: string;
}

/**
 * Loader ngx-translate. Compose le catalogue d'une langue à partir de trois
 * sources fusionnées par priorité croissante (la suivante écrase la précédente
 * au niveau des feuilles) :
 *   1. JSON statique embarqué (`assets/i18n`) — base de secours, toujours chargée ;
 *   2. catalogue public `GET /translation` — TOUTES les locales, un seul appel ;
 *   3. catalogue privé `GET /me/translation` — private + overrides du compte, si authentifié.
 *
 * Stratégie « 1 appel au boot + cache localStorage » (cf. i18n-cache.ts) :
 *   - le catalogue public est récupéré une seule fois (toutes locales) et
 *     mémoïsé pour toute la session ; il alimente donc tous les changements de
 *     langue SANS nouvel appel réseau ;
 *   - au démarrage, s'il est déjà en cache localStorage, il est servi
 *     instantanément (aucune attente réseau), la revalidation étant déclenchée
 *     en tâche de fond par LocaleService via {@link refreshPublicCatalogs} ;
 *   - le catalogue privé est mémoïsé par token (réutilisé au switch de langue,
 *     recalculé au changement de token, retiré au logout puisque le token
 *     disparaît du storage).
 */
export class ApiTranslateLoader implements TranslateLoader {
  /**
   * HttpClient construit sur HttpBackend : bypass TOTAL des intercepteurs.
   *
   * Indispensable : `getTranslation` est appelé pendant la construction même
   * de TranslateService (via `defaultLanguage` de `forRoot`). Or la chaîne
   * AuthenticationInterceptor → AuthenticationService → CurrentUserService →
   * BaseApiService ré-injecte TranslateService → NG0200 (app blanche au
   * bootstrap). Aucun service ne doit être tiré ici, même paresseusement.
   */
  private readonly rawHttp: HttpClient;

  /** Mémo du catalogue public toutes-locales (session entière). */
  private publicCatalogs$?: Observable<LocaleCatalogs>;

  /** Mémo du catalogue privé, invalidé au changement de token. */
  private privateMemo?: {
    token: string;
    catalogs$: Observable<LocaleCatalogs>;
  };

  constructor(backend: HttpBackend) {
    this.rawHttp = new HttpClient(backend);
  }

  getTranslation(lang: string): Observable<TranslationCatalog> {
    const token = this.readToken();

    return forkJoin({
      static: this.loadStatic(lang),
      public: this.publicCatalogs().pipe(map((all) => all[lang] ?? {})),
      private: token
        ? this.privateCatalogs(token).pipe(map((all) => all[lang] ?? {}))
        : of<TranslationCatalog>({}),
    }).pipe(
      map(({ static: base, public: pub, private: priv }) =>
        deepMerge(deepMerge(base, pub), priv)
      )
    );
  }

  /**
   * Force un rechargement réseau du catalogue public complet, met à jour le
   * cache localStorage et le mémo interne. Retourne `true` si `generated_at`
   * a changé depuis le cache précédent (→ l'appelant déclenche un reloadLang).
   *
   * Appelé par LocaleService en tâche de fond après un boot servi depuis le
   * cache : c'est la « vérification légère » qui évite de servir du périmé.
   */
  refreshPublicCatalogs(): Observable<boolean> {
    const previous = readCache()?.generatedAt;
    return this.fetchPublicResponse().pipe(
      map((response) => {
        const locales = response?.locales ?? {};
        const generatedAt = response?.generated_at ?? '';
        writeCache({ generatedAt, locales });
        this.publicCatalogs$ = of(locales);
        return previous !== undefined && generatedAt !== previous;
      }),
      // Échec réseau : on conserve le cache existant, rien ne change.
      catchError(() => of(false))
    );
  }

  /**
   * Catalogue public toutes-locales, mémoïsé. Le cache localStorage est
   * prioritaire (boot instantané) ; à défaut, un unique appel réseau le peuple
   * et l'écrit en cache.
   */
  private publicCatalogs(): Observable<LocaleCatalogs> {
    if (!this.publicCatalogs$) {
      const cached = readCache();
      this.publicCatalogs$ = cached
        ? of(cached.locales)
        : this.fetchPublicResponse().pipe(
            map((response) => {
              const locales = response?.locales ?? {};
              writeCache({
                generatedAt: response?.generated_at ?? '',
                locales,
              });
              return locales;
            }),
            catchError(() => of<LocaleCatalogs>({})),
            shareReplay(1)
          );
    }
    return this.publicCatalogs$;
  }

  /** Catalogue privé toutes-locales, mémoïsé par token. */
  private privateCatalogs(token: string): Observable<LocaleCatalogs> {
    if (!this.privateMemo || this.privateMemo.token !== token) {
      this.privateMemo = {
        token,
        catalogs$: this.rawHttp
          .get<TranslationCatalogResponse>(
            `${environment.apiBasePaths.data}/me/translation`,
            // Le header Authorization est posé manuellement : HttpBackend ne
            // passe pas par AuthenticationInterceptor (voulu, cf. NG0200).
            { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) }
          )
          .pipe(
            map((response) => response?.locales ?? {}),
            catchError(() => of<LocaleCatalogs>({})),
            shareReplay(1)
          ),
      };
    }
    return this.privateMemo.catalogs$;
  }

  private fetchPublicResponse(): Observable<TranslationCatalogResponse> {
    // Pas de `?locale=` : l'API renvoie toutes les locales en une réponse.
    return this.rawHttp.get<TranslationCatalogResponse>(
      `${environment.apiBasePaths.data}/translation`
    );
  }

  private loadStatic(lang: string): Observable<TranslationCatalog> {
    return this.rawHttp
      .get<TranslationCatalog>(`./assets/i18n/${lang}.json`)
      .pipe(catchError(() => of<TranslationCatalog>({})));
  }

  /**
   * Présence d'un token = même critère de stockage que
   * AuthenticationService.getJwtToken(), lu directement pour ne pas instancier
   * le service (cf. commentaire NG0200 ci-dessus). Un token expiré fera
   * simplement échouer /me/translation → catchError → {}.
   */
  private readToken(): string | null {
    return readStoredToken();
  }
}

/** Fusion profonde : `override` écrase `base` au niveau des feuilles. */
function deepMerge(
  base: TranslationCatalog,
  override: TranslationCatalog
): TranslationCatalog {
  const result: TranslationCatalog = { ...base };

  for (const [key, value] of Object.entries(override)) {
    const existing = result[key];
    result[key] =
      isCatalog(value) && isCatalog(existing)
        ? deepMerge(existing, value)
        : value;
  }

  return result;
}

function isCatalog(value: unknown): value is TranslationCatalog {
  return typeof value === 'object' && value !== null;
}
